import { RegistrationStatus } from '@exam-center/graphql';
import { ColorPalette, FormSelection, type FormSelectionProps } from '@shared/components';
import { type FieldValues } from 'react-hook-form';

import { CircleIndicatorLabel, type CircleIndicatorLabelProps } from '../circle-indicator-label';

import { registrationStatusColor, registrationStatusLabel } from './constants';

export type RegistrationStatusSelectionProps<TFieldValues extends FieldValues> = Omit<
  FormSelectionProps<TFieldValues>,
  'label' | 'options'
> & {
  options: RegistrationStatus[];
};

export function getColorAndLabelByStatus(
  status: RegistrationStatus,
  label: string,
): CircleIndicatorLabelProps {
  if (status === RegistrationStatus.RegistrationClosed) {
    return {
      label,
      color: 'grey',
      tone: 500,
    };
  }

  const color = registrationStatusColor[status] ?? ColorPalette.SECONDARY;

  return { color, label };
}

export default function RegistrationStatusSelection<TFieldValues extends FieldValues>(
  props: Readonly<RegistrationStatusSelectionProps<TFieldValues>>,
) {
  const { options: statusOptions, ...restProps } = props;

  const options = statusOptions.map((status) => ({
    value: status,
    label: registrationStatusLabel[status],
  }));

  return (
    <FormSelection
      {...restProps}
      options={options}
      label='จัดการสถานะ'
      sx={{ maxWidth: 240, width: '100%' }}
      itemComponent={({ option }) => {
        const status = option.value as RegistrationStatus;

        return <CircleIndicatorLabel {...getColorAndLabelByStatus(status, option.label)} />;
      }}
      slotProps={{
        select: {
          renderValue: (value) => {
            const status = value as RegistrationStatus;
            const label = options.find((option) => option.value === status)?.label ?? '';
            return <CircleIndicatorLabel {...getColorAndLabelByStatus(status, label)} />;
          },
        },
      }}
    />
  );
}
